import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, formatCurrency } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MoreHorizontal, Pencil, Trash2, CalendarIcon, TrendingUp, TrendingDown, Filter } from "lucide-react";
import { format, isWithinInterval, parseISO } from "date-fns";
import { toast } from "sonner";

const FILTER_MODES = [
  { label: "Current Cycle", value: "current" },
  { label: "Select Cycles", value: "cycles" },
  { label: "Custom Date Range", value: "custom" },
  { label: "All Transactions", value: "all" },
];

// Transaction Dialog
const TransactionDialog = ({ transaction, onClose }) => {
  const { categories, addTransaction, updateTransaction, cycleConfig } = useApp();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(transaction ? new Date(transaction.dateISO) : new Date());
  const [type, setType] = useState(transaction?.type || "expense");
  const [categoryId, setCategoryId] = useState(transaction?.categoryId || "");
  const [amount, setAmount] = useState(transaction?.amount?.toString() || "");
  const [notes, setNotes] = useState(transaction?.notes || "");
  const [loading, setLoading] = useState(false);

  const filteredCategories = categories.filter(c => c.type === type);
  const isEdit = !!transaction;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId || !amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const data = {
        dateISO: format(date, "yyyy-MM-dd"),
        type,
        categoryId,
        amount: parseFloat(amount),
        notes,
      };
      
      if (isEdit) {
        updateTransaction(transaction.id, data);
        toast.success("Transaction updated!");
      } else {
        addTransaction(data);
        toast.success("Transaction added!");
      }
      setOpen(false);
      if (onClose) onClose();
    } catch (error) {
      toast.error(isEdit ? "Failed to update transaction" : "Failed to add transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    setCategoryId("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil className="w-4 h-4 mr-2" />Edit
          </DropdownMenuItem>
        ) : (
          <Button className="rounded-full bg-stone-900 hover:bg-stone-800" data-testid="add-transaction-btn">
            <Plus className="w-4 h-4 mr-2" />Add Transaction
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isEdit ? "Edit Transaction" : "Add Transaction"}</DialogTitle>
          <DialogDescription>Enter the transaction details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={handleTypeChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {filteredCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Amount (£)</Label>
            <Input type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} min="0" step="0.01" required />
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea placeholder="Add a note..." value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Transaction" : "Add Transaction"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Dialog
const DeleteTransactionDialog = ({ transaction }) => {
  const [open, setOpen] = useState(false);
  const { deleteTransaction } = useApp();

  const handleDelete = () => {
    deleteTransaction(transaction.id);
    toast.success("Transaction deleted!");
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setOpen(true); }} className="text-rose-600">
        <Trash2 className="w-4 h-4 mr-2" />Delete
      </DropdownMenuItem>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
          <AlertDialogDescription>Are you sure? This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function Transactions() {
  const navigate = useNavigate();
  const { transactions, categories, selectedCycle, cycleKeys, cycleConfig, getCycleDateRange, getCategoryById } = useApp();
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [filterMode, setFilterMode] = useState("current");
  const [selectedCycles, setSelectedCycles] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  // Get all available cycles (deduplicated)
  const availableCycles = useMemo(() => {
    const cycleMap = new Map();
    
    // Add cycles from transactions
    transactions.forEach(t => {
      if (t.cycleKey && !cycleMap.has(t.cycleKey)) {
        cycleMap.set(t.cycleKey, t.cycleKey);
      }
    });
    
    // Add current custom cycle from config (if set)
    if (cycleConfig?.customStart && !cycleMap.has(cycleConfig.customStart)) {
      cycleMap.set(cycleConfig.customStart, cycleConfig.customStart);
    }
    
    return Array.from(cycleMap.keys()).sort().reverse();
  }, [transactions, cycleConfig]);

  // Toggle cycle selection
  const toggleCycleSelection = (cycleKey) => {
    setSelectedCycles(prev => {
      if (prev.includes(cycleKey)) {
        return prev.filter(c => c !== cycleKey);
      } else {
        return [...prev, cycleKey];
      }
    });
  };

  // Filter transactions based on mode
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];
    
    // Filter by date/cycle mode
    if (filterMode === "current" && cycleConfig?.customStart && cycleConfig?.customEnd) {
      const start = new Date(cycleConfig.customStart);
      const end = new Date(cycleConfig.customEnd);
      filtered = filtered.filter(t => {
        const txDate = new Date(t.dateISO);
        return txDate >= start && txDate <= end;
      });
    } else if (filterMode === "cycles" && selectedCycles.length > 0) {
      filtered = filtered.filter(t => selectedCycles.includes(t.cycleKey));
    } else if (filterMode === "custom") {
      filtered = filtered.filter(t => {
        const txDate = parseISO(t.dateISO);
        return isWithinInterval(txDate, { start: startDate, end: endDate });
      });
    }
    // "all" mode shows everything
    
    if (typeFilter !== "all") {
      filtered = filtered.filter(t => t.type === typeFilter);
    }
    if (categoryFilter !== "all") {
      filtered = filtered.filter(t => t.categoryId === categoryFilter);
    }
    
    return filtered.sort((a, b) => new Date(a.dateISO) - new Date(b.dateISO));
  }, [transactions, filterMode, cycleConfig, selectedCycles, startDate, endDate, typeFilter, categoryFilter]);

  // Filter transactions (no running balance needed)
  const filteredTransactionsList = filteredTransactions;

  // Calculate totals for filtered transactions
  const totalIncome = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalExpenses = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);

  const currentCycleRange = selectedCycle ? getCycleDateRange(selectedCycle) : null;
  
  // Today's date display
  const today = new Date();
  const todayFormatted = format(today, "EEEE, MMMM d, yyyy");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl text-stone-900">Transactions</h2>
          <p className="text-stone-500 mt-1">{currentCycleRange?.label || 'All cycles'}</p>
        </div>
        <TransactionDialog />
      </div>

      {/* Today's Date */}
      <Card className="p-3 bg-stone-100 rounded-xl border border-stone-200">
        <p className="text-sm text-stone-600 text-center">
          📅 Today: <strong>{todayFormatted}</strong>
        </p>
      </Card>

      {/* Filters */}
      <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-stone-500">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter By:</span>
            </div>
            
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter Mode" /></SelectTrigger>
              <SelectContent>
                {FILTER_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cycle Selection */}
          {filterMode === "cycles" && (
            <div className="pt-4 border-t border-stone-100">
              <p className="text-sm font-medium text-stone-600 mb-3">Select Cycles (can select multiple):</p>
              {availableCycles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availableCycles.map((cycleKey, index) => {
                    const range = getCycleDateRange(cycleKey);
                    const isSelected = selectedCycles.includes(cycleKey);
                    return (
                      <Button
                        key={`${cycleKey}-${index}`}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleCycleSelection(cycleKey)}
                        className={`rounded-full ${isSelected ? 'bg-stone-900' : ''}`}
                      >
                        {range?.label || cycleKey}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-stone-400">No cycles available. Set a cycle in Settings first.</p>
              )}
              {selectedCycles.length > 0 && (
                <p className="text-xs text-stone-500 mt-2">
                  {selectedCycles.length} cycle(s) selected
                </p>
              )}
            </div>
          )}

          {/* Custom Date Range */}
          {filterMode === "custom" && (
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-stone-100">
              <span className="text-sm text-stone-500">From:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px]">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(startDate, "dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(date) => date && setStartDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
              <span className="text-sm text-stone-500">To:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px]">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(endDate, "dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(date) => date && setEndDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-emerald-50 border-emerald-200 rounded-xl">
          <p className="text-xs font-semibold uppercase text-stone-500">Total Income</p>
          <p className="text-xl font-bold text-emerald-700">{formatCurrency(totalIncome)}</p>
        </Card>
        <Card className="p-4 bg-rose-50 border-rose-200 rounded-xl">
          <p className="text-xs font-semibold uppercase text-stone-500">Total Expenses</p>
          <p className="text-xl font-bold text-rose-700">{formatCurrency(totalExpenses)}</p>
        </Card>
        <Card className="p-4 bg-indigo-50 border-indigo-200 rounded-xl">
          <p className="text-xs font-semibold uppercase text-stone-500">Net</p>
          <p className={`text-xl font-bold ${totalIncome - totalExpenses >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>
            {formatCurrency(totalIncome - totalExpenses)}
          </p>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden">
        {filteredTransactionsList.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Notes</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactionsList.map((tx) => {
                  const category = getCategoryById(tx.categoryId);
                  return (
                    <TableRow key={tx.id} className="hover:bg-stone-50">
                      <TableCell className="font-mono text-sm">
                        {new Date(tx.dateISO).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                          tx.type === "income" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                        }`}>
                          {tx.type === "income" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {tx.type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span 
                          className="font-medium cursor-pointer hover:underline"
                          onClick={() => navigate(`/categories/${tx.categoryId}`)}
                        >
                          {category?.name || "Unknown"}
                        </span>
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${
                        tx.type === "income" ? "text-emerald-700" : "text-rose-700"
                      }`}>
                        {tx.type === "income" ? "+" : "-"}{formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell className="text-stone-500 max-w-[200px] truncate">{tx.notes || "-"}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <TransactionDialog transaction={tx} />
                            <DeleteTransactionDialog transaction={tx} />
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-stone-400">
            <p className="font-medium">No transactions found</p>
            <p className="text-sm">Add your first transaction to get started</p>
          </div>
        )}
      </Card>
    </div>
  );
}
